// src/pages/Administration/Members/MemberEditDrawer.tsx
/**
 * View and correct one member.
 *
 * Two halves, deliberately separated: the **record** (facts the church system owns, read-only) and the
 * **details** (what an administrator may correct). See `memberFields.ts` for why each field sits where
 * it does — in particular why `userId` is a fact and not a field.
 *
 * ⚠️ **There is no delete here, and no deactivate either.** Deactivating a member is this system's
 * closest equivalent to deleting them, and removing people was explicitly excluded from this screen.
 * `MemberService.updateStatus` exists and is deliberately not called.
 *
 * ⚠️ Saves with `patch`, carrying only the fields that actually changed. A whole-object PUT from here
 * would depend on the backend merging rather than replacing, which is an assumption this screen should
 * not make about somebody else's record.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Badge, Box, Button, Divider, Drawer, Group, Paper, Stack, Text,
  ThemeIcon, Title,
} from '@/components/ui';
// ⚠️ From `@/components`, not `@/components/ui` — the copy control lives in the advanced barrel.
import { CopyButton } from '@/components';
import {
  IconAlertTriangle, IconDeviceFloppy, IconIdBadge2, IconInfoCircle, IconLock, IconUser,
} from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useMemberService,
  type MaritalStatus, type Member, type MemberType,
} from '@actschurch/ccms-sdk';
import { getInitialsFromName, resolveAvatarSrc } from '@/utils/avatar';
import MemberForm from './MemberForm';
import {
  changedFields, formFromMember, validateForm,
  type MemberFormValues,
} from './memberFields';

/** One read-only fact about the record. */
function Fact({
  label, value, hint, copyable = false,
}: {
  label: string;
  value?: string | null;
  hint?: string;
  copyable?: boolean;
}) {
  const shown = (value ?? '').trim();
  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start" gap="md">
      <Box style={{ minWidth: 0 }}>
        <Text size="sm" c="dimmed">{label}</Text>
        {hint && <Text size="xs" c="dimmed">{hint}</Text>}
      </Box>
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
        <Text size="sm" fw={600} style={{ wordBreak: 'break-all' }}>
          {shown || '—'}
        </Text>
        {copyable && shown && <CopyButton value={shown} size="xs" variant="subtle" />}
      </Group>
    </Group>
  );
}

export interface MemberEditDrawerProps {
  member: Member | null;
  maritalStatuses: MaritalStatus[];
  memberTypes: MemberType[];
  opened: boolean;
  onClose: () => void;
  /** Called with the saved record so the caller can refresh its list in place. */
  onSaved: (updated: Member) => void;
}

export default function MemberEditDrawer({
  member, maritalStatuses, memberTypes, opened, onClose, onSaved,
}: MemberEditDrawerProps) {
  const memberService = useMemberService();

  const [values, setValues] = useState<MemberFormValues | null>(null);
  const [original, setOriginal] = useState<MemberFormValues | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof MemberFormValues, string>>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened || !member) return;
    const form = formFromMember(member);
    setValues(form);
    setOriginal(form);
    setErrors({});
  }, [opened, member]);

  const payload = useMemo(
    () => (values && original ? changedFields(original, values) : {}),
    [values, original],
  );
  const dirtyCount = Object.keys(payload).length;

  const typeName = memberTypes.find((t) => t.typeID === member?.typeID)?.name;

  const handleSave = async () => {
    if (!member || !values || dirtyCount === 0) return;

    const found = validateForm(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    try {
      // ⚠️ `patch`, never `update`. See the file header.
      // ⚠️ The cast is narrowing, not widening: `payload` is built only from `EDITABLE_FIELDS`, all of
      // which are real `Member` keys. `patch` types its body as `Partial<Member>`, which cannot express
      // "some of these keys" without losing the guarantee that nothing else can be in there.
      const res = await memberService.patch(member.memberID, payload as Partial<Member>);
      // Read through the envelope, as elsewhere in this app.
      const saved = ((res as unknown as { data?: Member })?.data ?? res) as Member;
      const merged: Member = { ...member, ...saved };

      setOriginal(formFromMember(merged));
      onSaved(merged);
      toast.success(
        'Saved',
        `${dirtyCount} ${dirtyCount === 1 ? 'detail' : 'details'} updated for ${merged.firstName ?? 'this member'}.`,
      );
    } catch (e) {
      console.error('[Members] save failed:', e);
      toast.error('Could not save', e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const fullName = [member?.firstName, member?.lastName].filter(Boolean).join(' ').trim();

  return (
    <Drawer
      opened={opened}
      // Locked while saving: closing mid-write leaves the admin unsure what landed.
      onClose={saving ? () => {} : onClose}
      position="right"
      size="xl"
      title={<Text fw={600}>Member details</Text>}
    >
      {!member || !values ? (
        <Alert type="info" icon={<IconInfoCircle size={16} />}>
          <Text size="sm">Nothing selected.</Text>
        </Alert>
      ) : (
        <Stack gap="lg">
          <Group gap="md" wrap="nowrap">
            <Avatar src={resolveAvatarSrc(member.avatar)} radius="xl" size="lg">
              {getInitialsFromName(fullName)}
            </Avatar>
            <Box style={{ minWidth: 0 }}>
              <Title order={4}>{fullName || 'Unnamed member'}</Title>
              <Group gap={6} mt={4}>
                {typeName && <Badge size="sm" variant="light">{typeName}</Badge>}
                <Badge size="sm" variant="light" color={member.isActive ? 'teal' : 'gray'}>
                  {member.isActive ? 'Active' : 'Not active'}
                </Badge>
                {member.isMember && <Badge size="sm" variant="light" color="blue">Member</Badge>}
              </Group>
            </Box>
          </Group>

          <Paper withBorder radius="md" p="md">
            <Group gap="sm" mb="xs">
              <ThemeIcon variant="light" color="gray" size="sm"><IconLock size={14} /></ThemeIcon>
              <Box>
                <Text fw={600} size="sm">Record</Text>
                <Text size="xs" c="dimmed">
                  Owned by the church system. Shown so it can be quoted for support, never changed here.
                </Text>
              </Box>
            </Group>
            <Divider mb="sm" />
            <Stack gap="xs">
              <Fact label="Member ID" value={String(member.memberID)} copyable />
              <Fact
                label="User ID"
                hint="The account this record belongs to."
                value={member.userId}
                copyable
              />
              <Fact label="Member code" value={member.memberCode} />
              <Fact label="Member type" value={typeName ?? String(member.typeID)} />
              {member.planningCentrePersonId && (
                <Fact
                  label="Planning Centre"
                  hint="Kept in step by the sync."
                  value={member.planningCentrePersonId}
                />
              )}
            </Stack>
          </Paper>

          <Paper withBorder radius="md" p="md">
            <Group gap="sm" mb="xs">
              <ThemeIcon variant="light" size="sm"><IconUser size={14} /></ThemeIcon>
              <Box>
                <Text fw={600} size="sm">Details</Text>
                <Text size="xs" c="dimmed">
                  Correct a name, a contact number or a date. Only what you change is sent.
                </Text>
              </Box>
            </Group>
            <Divider mb="md" />
            <MemberForm
              values={values}
              errors={errors}
              maritalStatuses={maritalStatuses}
              disabled={saving}
              onChange={(patch) => setValues((prev) => (prev ? { ...prev, ...patch } : prev))}
            />
          </Paper>

          <Alert type="info" icon={<IconIdBadge2 size={16} />}>
            <Text size="sm">
              Members cannot be removed from this screen. To stop someone appearing in Acts Care, take
              away their roles under Administration → Users instead.
            </Text>
          </Alert>

          {dirtyCount > 0 && (
            <Alert type="warning" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">
                {dirtyCount} unsaved {dirtyCount === 1 ? 'change' : 'changes'}. Closing this panel
                discards them.
              </Text>
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose} disabled={saving}>Close</Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              loading={saving}
              disabled={dirtyCount === 0}
            >
              {dirtyCount > 0 ? `Save ${dirtyCount}` : 'Save'}
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}
